/**
 * PromoStrip — Top announcement bar above Navbar.
 *
 * Reads from site_content.promo_strip { enabled, message, link_label, link_url,
 * bg_color, text_color, dismissible }.
 * If dismissible, stores dismissal in localStorage (per-message hash).
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { useFeatureFlag } from "@/lib/featureFlags";

const STORAGE_KEY = "ms_promo_dismissed_v1";

export default function PromoStrip() {
    const site = useSiteContent();
    const flagEnabled = useFeatureFlag("show_promo_strip", true);
    const ps = site?.promo_strip;
    const [dismissed, setDismissed] = useState(false);

    // Check localStorage dismissal (per-message)
    useEffect(() => {
        if (!ps || !ps.enabled || !ps.message) return;
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw && raw === ps.message) setDismissed(true);
        } catch { /* ignore */ }
    }, [ps]);

    if (!flagEnabled) return null;
    if (!ps || !ps.enabled || !ps.message || dismissed) return null;

    const handleDismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, ps.message); } catch { /* ignore */ }
        setDismissed(true);
    };

    const isInternal = ps.link_url && (ps.link_url.startsWith("/") || ps.link_url.startsWith("#"));

    return (
        <div
            data-testid="promo-strip"
            className="w-full text-sm font-medium relative z-40"
            style={{ background: ps.bg_color || "#0ea5e9", color: ps.text_color || "#ffffff" }}
        >
            <div className="container mx-auto px-4 md:px-6 max-w-7xl py-2 flex items-center justify-center gap-3 flex-wrap text-center">
                <span>{ps.message}</span>
                {(ps.link_label && ps.link_url) && (
                    isInternal ? (
                        <Link to={ps.link_url} className="underline font-bold hover:opacity-80">{ps.link_label} →</Link>
                    ) : (
                        <a href={ps.link_url} target="_blank" rel="noreferrer" className="underline font-bold hover:opacity-80">{ps.link_label} →</a>
                    )
                )}
            </div>
            {ps.dismissible && (
                <button onClick={handleDismiss} data-testid="promo-dismiss" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10" aria-label="Kapat">
                    <X className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}
